import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

export default function Contatti() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-white/[0.05] px-5 py-4 text-base text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-primary focus:bg-white/[0.08] focus:ring-2 focus:ring-primary/30";

  return (
    <section id="contatti" data-testid="contatti-section" className="py-28 md:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
            {t.contact.label}
          </p>
          <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-6xl">
            {t.contact.title}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/65 md:text-lg">
            {t.contact.sub}
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                <Mail size={18} />
              </span>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                  {t.contact.emailLabel}
                </div>
                <a
                  data-testid="contact-email-link"
                  href="mailto:you4mada@gmail.com"
                  className="mt-1 block text-lg text-white transition-colors hover:text-primary"
                >
                  you4mada@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                <MapPin size={18} />
              </span>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                  {t.contact.officeLabel}
                </div>
                <div className="mt-1 text-lg text-white" data-testid="contact-offices">
                  {t.contact.offices.join(" · ")}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="flex flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:p-10"
        >
          <input
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t.contact.name}
            className={inputCls}
          />
          <input
            data-testid="contact-email-input"
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder={t.contact.email}
            className={inputCls}
          />
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder={t.contact.message}
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="mt-2 rounded-full bg-primary px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a] disabled:opacity-60"
          >
            {sending ? t.contact.sending : t.contact.send}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
